import type { GlideFC } from "@glide/common";
import { CheckboxContainer, CheckboxWrapper, CheckboxIcon } from "@glide/common-components";
import * as React from "react";
import { useState } from "react";
import { useBuilderTheme } from "../../hooks/use-builder-theme";
import WindowedModalV2 from "../windowed-modal/windowed-modal";
import { getFormattedTimeDifference } from "@glide/data-types";
import { Button } from "../button/button";

export interface AppToDelete {
    name: string;
    updatedAt?: Date;
}

interface BatchDeleteModalProps {
    apps: AppToDelete[];
    onConfirm: () => void;
    onCancel: () => void;
}

const CONFIRMATION_TEXT = "I know what I'm doing when deleting all these apps forever and ever";

export const BatchDeleteModal: GlideFC<BatchDeleteModalProps> = props => {
    const { apps, onConfirm, onCancel } = props;
    const [challenge, setChallenge] = useState("");
    const [isAbsolutelyCertain, setIsAbsolutelyCertain] = useState(false);
    const theme = useBuilderTheme();

    const isTextMatched = challenge === CONFIRMATION_TEXT;
    const canSubmit = isTextMatched && isAbsolutelyCertain;

    return (
        <WindowedModalV2
            maxWidth="520px"
            title="Delete Multiple Apps?"
            canClose={true}
            className="click-outside-ignore"
            footer={
                <div tw="flex justify-end gap-0.5">
                    <Button
                        label="Cancel"
                        onClick={onCancel}
                        buttonType="tertiary"
                        variant="default"
                        size="md"
                    />
                    <Button
                        label="Delete Apps"
                        onClick={onConfirm}
                        buttonType="primary"
                        variant="danger"
                        size="md"
                        disabled={!canSubmit}
                    />
                </div>
            }
            onClose={onCancel}>
            <div>
                <p tw="text-sm mb-4">
                    You are about to permanently delete <strong>{apps.length}</strong> app
                    {apps.length !== 1 ? "s" : ""}:
                </p>

                <div tw="max-h-32 overflow-y-auto bg-n100 rounded p-3 mb-4">
                    <ul tw="text-sm space-y-1">
                        {apps.map((app, index) => (
                            <li key={index}>
                                • {app.name}{" "}
                                {app.updatedAt && (
                                    <span tw="text-text-pale">({getFormattedTimeDifference(app.updatedAt)})</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>

                <p tw="text-sm text-r400 font-medium mb-4">
                    <span role="img" aria-label="warning">
                        ⚠️
                    </span>{" "}
                    Your apps will be permanently deleted with no chance of recovery. This cannot be undone.
                </p>

                <div tw="mb-4">
                    <p tw="mb-2 text-sm">
                        Type{" "}
                        <span tw="bg-separator-color text-xs rounded py-1 px-2 font-mono">{CONFIRMATION_TEXT}</span> to
                        confirm:
                    </p>
                    <input
                        autoFocus
                        tw="bg-separator-color rounded-lg text-sm py-2 px-3 w-full"
                        type="text"
                        value={challenge}
                        onChange={e => setChallenge(e.target.value)}
                        placeholder="Type confirmation text..."
                    />
                </div>

                {isTextMatched && (
                    <label tw="flex items-center gap-3 p-4 bg-y100 rounded-lg border border-y300 shadow-sm cursor-pointer hover:bg-y200 transition-colors">
                        <CheckboxWrapper
                            onClick={(e: React.MouseEvent<HTMLLabelElement>) => {
                                e.stopPropagation();
                            }}
                            disablePadding={true}
                            isEnabled={true}
                            className={`${isAbsolutelyCertain ? "changed" : "default"} ${
                                isAbsolutelyCertain ? "checked" : "unchecked"
                            }`}>
                            <input
                                type="checkbox"
                                checked={isAbsolutelyCertain}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    e.stopPropagation();
                                    setIsAbsolutelyCertain(!isAbsolutelyCertain);
                                }}
                            />
                            <CheckboxContainer disablePadding={true} color={theme.accentPrimary}>
                                <CheckboxIcon defaultColor="white" />
                            </CheckboxContainer>
                        </CheckboxWrapper>
                        <span tw="text-sm font-semibold text-text-dark">Are you absolutely certain?</span>
                    </label>
                )}
            </div>
        </WindowedModalV2>
    );
};
